import { Component, Renderer2, TemplateRef } from '@angular/core';
import { HistoryServicesService } from 'src/app/services/history-services/history-services.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ActivatedRoute } from '@angular/router';
import { TuiAlertService } from '@taiga-ui/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import {
  descriptionComparator,
  FreelancePoolConfiguration,
  HeaderTableInfo,
  OperativeZoneConfiguration,
  ProductData,
  ProductInformation,
  ProductInformationItem,
  timeLimitComparator,
} from 'src/app/models/pool-parameters/pool-parameters.model';
import { PoolManagerService } from 'src/app/services/pool-manager/pool-manager.service';
import { VerticalType } from 'src/app/enum/vertical-type/verticalType.enum';
import { Zone } from 'src/app/models/zone/zone.model';
import { EditOperativeZoneModalComponent } from './components/edit-operative-zone-modal/edit-operative-zone-modal.component';

export enum HeaderKey {
  SERVICE_CONDITIONS = 'description',
  TIME_LIMIT = 'timeLimit',
  ACTIONS = 'ACTIONS',
  RANGE_KM = 'productDescription',
  SUBPRODUCT = 'productDescription',
  PRICE = 'poolPrice',
  MARGIN = 'poolProfitMargin',
}

export enum HeaderTooltipKey {
  PRICE = 'poolPrice',
  MARGIN = 'poolProfitMargin',
}

@Component({
  selector: 'app-pool-parameters',
  templateUrl: './pool-parameters.component.html',
  styleUrls: ['./pool-parameters.component.css'],
})
export class PoolParametersComponent {
  HeaderKey = HeaderKey;

  OperativeZoneHeaders: HeaderTableInfo[] = [];
  OperativeZoneHeadersKeys: (keyof OperativeZoneConfiguration)[] = [];

  zones!: Zone[];
  activeZone!: string;
  zoneId!: string;
  clientId: string = '';
  clientName: string = '';

  PoolZoneConfiguration: FreelancePoolConfiguration = {
    freelancePoolConfiguration: [],
  };

  productsDetails: ProductInformation = {
    productInformationData: [],
    currencySymbol: '',
  };

  constructor(
    public historySvc: HistoryServicesService,
    private modalService: BsModalService,
    private route: ActivatedRoute,
    private readonly alerts: TuiAlertService,
    private renderer: Renderer2,
    public hs: HistoryServicesService,
    public translate: TranslateService,
    private poolSvc: PoolManagerService
  ) {}

  ngOnInit(): void {
    this.loadHeaders();

    this.route.queryParamMap.subscribe((params) => {
      this.clientId = params.get('userId') || '';
      this.clientName = params.get('clientName') || '';
    });
    this.historySvc.getZones().subscribe((res: any) => {
      this.zones = res.zones;
    });

    this.sorter$.subscribe(() => this.sortDataOperativeZone());
    this.direction$.subscribe(() => this.sortDataOperativeZone());
  }

  ngOnDestroy(): void {
    this.sorter$.unsubscribe();
    this.direction$.unsubscribe();
  }

  // SORTING

  readonly direction$ = new BehaviorSubject<-1 | 1>(1);
  readonly sorter$ = new BehaviorSubject<
    keyof OperativeZoneConfiguration | any
  >(this.OperativeZoneHeadersKeys[0]);

  sortedDataOperativeZone$ = new BehaviorSubject<OperativeZoneConfiguration[]>(
    []
  );

  private sortDataOperativeZone() {
    if (this.PoolZoneConfiguration?.freelancePoolConfiguration) {
      const sorted = [
        ...this.PoolZoneConfiguration.freelancePoolConfiguration,
      ].sort((a: OperativeZoneConfiguration, b: OperativeZoneConfiguration) => {
        const aValue =
          a[this.sorter$.value as keyof OperativeZoneConfiguration];
        const bValue =
          b[this.sorter$.value as keyof OperativeZoneConfiguration];

        if (aValue == null || bValue == null) return 0;

        const result = aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        return this.direction$.value === 1 ? result : -result;
      });

      this.sortedDataOperativeZone$.next(sorted);
    }
  }

  // NG FOR TRACK BY
  trackByHeaderTableInfo(index: number, header: HeaderTableInfo): string {
    return header.Key!;
  }
  trackByOperativeZoneConfiguration(
    index: number,
    header: OperativeZoneConfiguration
  ): string {
    return header.operativeZoneConfigurationGuid!;
  }

  // EDIT MODALS
  openUpdateOperativeZoneModal(
    operativeZone: OperativeZoneConfiguration,
    index: number
  ) {
    let config = {
      class: 'modal-class-template-400',
      backdrop: true,
      initialState: {
        operativeZone: operativeZone,
      },
    };
    const modalRef = this.modalService.show(
      EditOperativeZoneModalComponent,
      config
    );
    modalRef?.content?.onClose.subscribe((result) => {
      if (!result) return;
      this.updateOperativeZone(result);
    });
  }

  addTableStatus(respuesta: ProductInformation): ProductInformation {
    respuesta.productInformationData.map((x: ProductInformationItem) => {
      x.tableOpen = true;
    });
    return respuesta;
  }

  getProductGuidsByVerticalType(verticalType: VerticalType): string[] {
    const verticalItem = this.productsDetails.productInformationData.find(
      (item) => item.verticalType === verticalType
    );
    if (
      !verticalItem ||
      !verticalItem.productData ||
      verticalItem.productData.length === 0
    ) {
      return [];
    }
    return verticalItem.productData.map(
      (product) => product.freelancePoolConfigurationGuid
    );
  }

  // LOAD DATA
  async filterData() {
    if (this.activeZone) {
      this.showLoader();
      try {
        await firstValueFrom(
          this.poolSvc.getOperativeConfigurationByZone(this.activeZone)
        ).then((res: FreelancePoolConfiguration) => {
          this.PoolZoneConfiguration = res;
          this.sortDataOperativeZone();
        });
        await firstValueFrom(
          this.poolSvc.getPoolProductsByZone(this.activeZone)
        ).then((res: ProductInformation) => {
          this.productsDetails = this.addTableStatus(res);
        });

        this.hideLoader();
      } catch (error) {
        this.hideLoader();
      }
    }
  }

  // SAVE DATA
  async updateAvailability(availability: boolean, verticalType: number) {
    const guids = this.getProductGuidsByVerticalType(verticalType);
    const body = {
      freelancePoolConfigurationGuids: guids,
      isAvailable: availability,
    };
    this.showLoader();
    try {
      await firstValueFrom(
        this.poolSvc.updatePoolConfigurationAvailability(body)
      ).then(async (res: any) => {
        if (res.isSuccess) {
          await firstValueFrom(
            this.poolSvc.getPoolProductsByZone(this.activeZone)
          ).then((res: ProductInformation) => {
            this.productsDetails = this.addTableStatus(res);
          });
        }
        this.showDepositAlert(res.isSuccess);
      });
      this.hideLoader();
    } catch (error: any) {
      this.showErrorCode(error.error.code);
      this.hideLoader();
    }
  }

  async updateSubproductData(event: ProductData) {
    const body = event;
    this.showLoader();
    try {
      await firstValueFrom(
        this.poolSvc.updateFreelancePoolConfigurationPrice(body)
      ).then(async (res: any) => {
        if (res.isSuccess) {
          const body = {
            zoneGuid: this.activeZone,
          };
          await firstValueFrom(
            this.poolSvc.getPoolProductsByZone(this.activeZone)
          ).then((res: ProductInformation) => {
            this.productsDetails = this.addTableStatus(res);
          });
        }
        this.showDepositAlert(res.isSuccess);
        this.hideLoader();
      });
    } catch (error: any) {
      this.showErrorCode(error.error.code);
      this.hideLoader();
    }
  }

  async updateOperativeZone(operativeZone: OperativeZoneConfiguration) {
    let body = operativeZone;
    this.showLoader();
    try {
      await firstValueFrom(
        this.poolSvc.updateOperativeZoneConfiguration(body)
      ).then(async (res: any) => {

        if (res.isSuccess) {
          const body = {
            zoneGuid: this.activeZone,
          };
          await firstValueFrom(
            this.poolSvc.getOperativeConfigurationByZone(this.activeZone)
          ).then((res: FreelancePoolConfiguration) => {
            this.PoolZoneConfiguration = res;
            this.sortDataOperativeZone();
          });
        }

        this.showDepositAlert(res.isSuccess);
        this.hideLoader();
      });
    } catch (error: any) {
      this.hideLoader();
      this.showErrorCode(error.error.code);
    }
  }

  // LOADING DISPLAY

  div_loder = document.getElementById('loader');
  showLoader() {
    if (this.div_loder) {
      this.renderer.setStyle(this.div_loder, 'display', 'flex');
    }
  }
  hideLoader() {
    if (this.div_loder) {
      this.renderer.setStyle(this.div_loder, 'display', 'none');
    }
  }

  // HEADERS
  private loadHeaders(): void {
    combineLatest([
      this.translate.stream([
        'pool_parameters.table_operative_zone.service_condition',
        'pool_parameters.table_operative_zone.time_limit',
        'pool_parameters.PRODUCT_HEADERS.5',
      ]),
      this.translate.stream([
        'pool_parameters.PRODUCT_HEADERS.0',
        'pool_parameters.PRODUCT_HEADERS.1',
        'pool_parameters.PRODUCT_HEADERS.2',
        'pool_parameters.PRODUCT_HEADERS.3',
        'pool_parameters.PRODUCT_HEADERS.4',
      ]),
    ]).subscribe(([operative, product]) => {
      this.OperativeZoneHeaders = [
        {
          Title: operative['pool_parameters.table_operative_zone.service_condition'],
          Sticky: true,
          Key: HeaderKey.SERVICE_CONDITIONS,
          Class: 'th-style',
          Size: 'm',
          Sortable: descriptionComparator,
        },
        {
          Title: operative['pool_parameters.table_operative_zone.time_limit'],
          Sticky: true,
          Key: HeaderKey.TIME_LIMIT,
          Class: 'th-style',
          Size: 'm',
          Sortable: timeLimitComparator,
        },
        {
          Title: operative['pool_parameters.PRODUCT_HEADERS.5'],
          Sticky: true,
          Key: HeaderKey.ACTIONS,
          Class: 'th-style',
          Size: 'm',
          Sortable: null,
        },
      ];
      this.setHeaderKeys();
    });
  }

  setHeaderKeys() {
    this.OperativeZoneHeadersKeys = this.OperativeZoneHeaders?.map(
      (x: HeaderTableInfo): keyof OperativeZoneConfiguration => {
        return x.Key ?? '';
      }
    );
  }

  // ALERTS
  showErrorCode(code: any) {
    this.alerts
      .open('', {
        label: this.translate.instant('pool_parameters.ERROR.TITLE'),
        status: 'error',
        autoClose: true,
      })
      .subscribe();
  }

  showDepositAlert(success: boolean): void {
    if (success) {
      this.alerts
        .open(
          this.translate.instant('pool_parameters.DEPOSIT.SUCCESS.MESSAGE'),
          {
            label: this.translate.instant(
              'pool_parameters.DEPOSIT.SUCCESS.TITLE'
            ),
            status: 'success',
            autoClose: true,
          }
        )
        .subscribe();
    } else {
      this.alerts
        .open(this.translate.instant('pool_parameters.DEPOSIT.ERROR.MESSAGE'), {
          label: this.translate.instant('pool_parameters.DEPOSIT.ERROR.TITLE'),
          status: 'error',
          autoClose: true,
        })
        .subscribe();
    }
  }

  getOperativeZoneDescription(description: string): string {
    const keys = [
      `pool-manager.pool-parameters.${description}`,
      `POOL_PARAMETERS.${description}`,
      `pool_parameters.${description}`,
    ];

    for (const key of keys) {
      const translation = this.translate.instant(key);
      if (translation !== key) {
        return translation;
      }
    }

    return description;
  }
}
